/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { useHistory, useParams } from 'react-router-dom';
import {
  useCreateInsightDashboardMutation,
  useGetInsightDashboardsByInitiativeQuery,
} from '../../../../../api/insight-dashboards';
import { ROUTES } from '../../../../../constants/routes';
import { useAppSelector } from '../../../../../reducers';
import { canUseStaticCustomDashboards } from '../../../../../selectors/initiative';
import { canManageCurrentLevel, isStaff } from '../../../../../selectors/user';
import { FeaturePermissions } from '../../../../../services/permissions/FeaturePermissions';
import { generateUrl } from '../../../../util';
import { InsightPage } from '../../utils/constants';
import { getInsightPage, getInsightPageOptions, isInsightLayoutPage } from '../../utils/helpers';
import {
  AddDashboardOption,
  InsightDashboardOption,
  TOOLTIP_MESSAGE,
  getDashboardOption,
  isDemoOption,
  staffOnlyOptions,
} from '../../utils/sidebar';
import { InsightsSidebar } from './InsightsSidebar';
import Loader from '@components/loader';

interface CompanyInsightsSidebarProps {
  isPortfolioTracker?: boolean;
  /** Inline buttons that need to be merged with dashboard buttons **/
  alignButtonRow: boolean;
}

export const CompanyInsightsSidebar = (props: CompanyInsightsSidebarProps) => {
  const { isPortfolioTracker = false, alignButtonRow } = props;

  const history = useHistory();
  const params = useParams<{
    initiativeId?: string;
    surveyId?: string;
    summaryPage?: InsightPage;
    dashboardId?: string;
  }>();
  const { initiativeId = '', dashboardId = '', summaryPage: summaryPageParam = '', surveyId = 'current' } = params;

  const canManage = useAppSelector(canManageCurrentLevel);
  const canAccessCustomDashboards = useAppSelector(FeaturePermissions.canAccessCustomDashboards);
  const isUserStaff = useAppSelector(isStaff);
  const canUseStaticESG = useAppSelector(canUseStaticCustomDashboards);
  // Cannot use ESG pages yet, but is staff
  const showStaffOnlyFeatures = isUserStaff && !canUseStaticESG;

  const insightPageOptions: InsightDashboardOption[] = getInsightPageOptions();

  const { data: dashboards, isFetching } = useGetInsightDashboardsByInitiativeQuery(initiativeId, {
    skip: !initiativeId || isPortfolioTracker,
  });
  const dashboardOptions: InsightDashboardOption[] = (dashboards || []).map((dashboard) => ({
    value: dashboard._id,
    label: dashboard.title,
    isCustom: true,
    disabled: !canAccessCustomDashboards,
    tooltip: !canAccessCustomDashboards ? TOOLTIP_MESSAGE.NOT_AVAILABLE_PLAN : undefined,
    isSharedByParent: initiativeId !== dashboard.initiativeId,
  }));

  const addDashboardOption = getDashboardOption(canAccessCustomDashboards);
  const options = [
    ...(showStaffOnlyFeatures ? staffOnlyOptions : []),
    ...insightPageOptions,
    ...dashboardOptions,
    ...(canManage ? [addDashboardOption] : []),
  ];

  const summaryPage =
    isInsightLayoutPage(summaryPageParam) || isDemoOption(summaryPageParam) ? summaryPageParam : undefined;
  const currentPage = dashboardId ? dashboardId : getInsightPage(summaryPage);

  const handleNavigate = (page: string) => {
    const route = isPortfolioTracker ? ROUTES.PORTFOLIO_TRACKER_COMPANY : ROUTES.SUMMARY;

    const url = generateUrl(route, {
      ...params,
      surveyId,
      summaryPage: page !== InsightPage.Overview ? page : undefined,
    });
    history.push(url);
  };

  const handleNavigateCustom = (dashboardId: string) => {
    const url = generateUrl(ROUTES.CUSTOM_DASHBOARD, { ...params, dashboardId });
    history.push(url);
  };

  const handleNavigateStaffOnly = (summaryPage: string) => {
    const url = generateUrl(ROUTES.SUMMARY_STAFF_ONLY, { ...params, summaryPage });
    history.push(url);
  };

  const [createInsightDashboard, { isLoading }] = useCreateInsightDashboardMutation();

  const handleAddDashboard = async () => {
    if (!initiativeId) {
      return;
    }

    const dashboard = await createInsightDashboard({ initiativeId, title: 'Custom Dashboard' }).unwrap();

    handleNavigateCustom(dashboard._id);
  };

  const handleClickOption = (item: InsightDashboardOption | AddDashboardOption) => () => {
    if (item.disabled) {
      return;
    }
    if (item.value === addDashboardOption.value) {
      return handleAddDashboard();
    }

    if (item.isStaffOnly) {
      return handleNavigateStaffOnly(item.value);
    }

    return item.isCustom ? handleNavigateCustom(item.value) : handleNavigate(item.value);
  };

  return (
    <>
      {isLoading || isFetching ? <Loader /> : null}
      <InsightsSidebar
        options={options}
        currentPage={currentPage}
        handleClickOption={handleClickOption}
        className={alignButtonRow ? 'align-button-row' : ''}
      />
    </>
  );
};
